import { html, nothing, type ReactiveController, type ReactiveControllerHost, type TemplateResult } from 'lit';
import { live } from 'lit/directives/live.js';

import '@awesome.me/webawesome/dist/components/radio-group/radio-group.js';
import '@awesome.me/webawesome/dist/components/radio/radio.js';

import { renderFormField } from '../../../../../helpers/form-field.js';
import { formatCredits } from '../../../../../helpers/public-ledger-credits.js';
import {
  EMPTY_DRAFT,
  amountsFromDraft,
  companyThresholdFloorText,
  creditsWorthText,
  parsePoundsToPence,
  parseWhole,
  penceBuysText,
  settingsProblemsFromMessage,
  validateSettings,
  type AmountField,
  type AutoAmounts,
  type SettingsDraft,
  type SetupFigures,
} from '../../../../../helpers/auto-payments.js';

// The input each amount is typed in, for focusing the ones a change was started for
const INPUT_ID: Record<AmountField, string> = {
  companyThresholdCredits: 'company-threshold',
  companyTargetPence: 'company-target',
  operatingThresholdCredits: 'operating-threshold',
  operatingTargetCredits: 'operating-target',
  monthlyCardLimitPence: 'card-limit',
  monthlyTopUpLimitPence: 'top-up-limit',
  targetAlertPercent: 'alert-percent',
};

/**
 * The automatic payments amounts form (#25): the company account's threshold (credits), target (£) and monthly card
 * limit (£), the operating account's threshold and target (credits) and monthly top-up limit (£), and whether to be told
 * when prices move. Each amount shows its conversion at today's price. Used by Set up automatic payments and, from phase 6,
 * Change automatic payments amounts, which starts it from the live amounts.
 *
 * A controller rather than an element, so the form's panels render in the job card's own shadow root and take its
 * `.job-panel` and form-field styles (the card includes `nsFormFieldStyles`). It holds the typed text and the problems by
 * field: the card's own checks (the same rules and wording as the server's), or the server's after a refusal.
 */
export class AutopaySettingsForm implements ReactiveController {
  draft: SettingsDraft = { ...EMPTY_DRAFT };

  // Problems by field: the form's own checks, or the server's after the card submitted
  problems: Partial<Record<AmountField, string>> = {};

  // Whether the card has tried to submit: until then a field shows a problem only once it has text
  private __tried = false;

  constructor(
    private readonly host: ReactiveControllerHost & HTMLElement,
    private readonly options: { figures: () => SetupFigures | null; busy: () => boolean },
  ) {
    host.addController(this);
  }

  hostConnected() {}

  /** Starts the form from these texts (a change starts from the live amounts) and clears any problems. */
  start(draft: SettingsDraft) {
    this.draft = { ...draft };
    this.problems = {};
    this.__tried = false;
    this.host.requestUpdate();
  }

  /** Moves focus to the first of these fields' inputs, once rendered. */
  async focus(fields: AmountField[]) {
    await this.host.updateComplete;
    const [field] = fields;
    if (!field) return;
    const input = this.host.shadowRoot?.getElementById(INPUT_ID[field]) as HTMLInputElement | null;
    input?.focus();
  }

  /**
   * The amounts to submit, or null when the form has a problem (each shown beside its field). Marks the form tried, so
   * every problem shows from now on.
   */
  submit(): AutoAmounts | null {
    this.__tried = true;
    this.problems = this.__check(true);
    this.host.requestUpdate();
    const parsed = amountsFromDraft(this.draft);
    if (!parsed.ok || Object.keys(this.problems).length > 0) return null;
    return parsed.amounts;
  }

  /**
   * Shows the server's PUBLIC_LEDGER_AUTOPAY_SETTINGS_INVALID message beside the fields it names; false when it names none
   * (the card then shows the whole message).
   */
  showServerProblems(message: string): boolean {
    const byField = settingsProblemsFromMessage(message);
    this.problems = byField;
    this.host.requestUpdate();
    return Object.keys(byField).length > 0;
  }

  private __set(key: keyof SettingsDraft, value: string | boolean | null) {
    this.draft = { ...this.draft, [key]: value };
    this.problems = this.__check(false);
    this.host.requestUpdate();
  }

  // The format of each amount, then the rules between them at today's price
  private __check(all: boolean): Partial<Record<AmountField, string>> {
    const parsed = amountsFromDraft(this.draft);
    const problems: Partial<Record<AmountField, string>> = {};
    const figures = this.options.figures();
    if (!parsed.ok) {
      for (const p of parsed.problems) problems[p.field] ??= p.reason;
    } else if (figures?.unitPricePence) {
      const { unitPricePence, minCredits, reserveCredits, minCompanyThresholdPence } = figures;
      for (const p of validateSettings(parsed.amounts, {
        unitPricePence,
        minCredits,
        reserveCredits,
        minCompanyThresholdPence,
      })) {
        problems[p.field] ??= p.reason;
      }
    }
    if (all || this.__tried) return problems;
    // Before the first submit, a field shows its own problem only once something is typed in it
    const typed: Partial<Record<AmountField, string>> = {};
    const textOf: Record<AmountField, string> = {
      companyThresholdCredits: this.draft.companyThresholdCredits,
      companyTargetPence: this.draft.companyTarget,
      operatingThresholdCredits: this.draft.operatingThresholdCredits,
      operatingTargetCredits: this.draft.operatingTargetCredits,
      monthlyCardLimitPence: this.draft.monthlyCardLimit,
      monthlyTopUpLimitPence: this.draft.monthlyTopUpLimit,
      targetAlertPercent: this.draft.alertOn ? this.draft.alertPercent : '',
    };
    for (const [field, reason] of Object.entries(problems) as [AmountField, string][]) {
      if (textOf[field].trim()) typed[field] = reason;
    }
    return typed;
  }

  private __renderInput(
    label: string,
    key: keyof SettingsDraft,
    field: AmountField,
    hint: string | null,
    inputmode: 'numeric' | 'decimal',
  ): TemplateResult {
    const id = INPUT_ID[field];
    const error = this.problems[field];
    return renderFormField({
      id,
      label,
      hint: hint ?? undefined,
      error,
      control: html`
        <input
          id=${id}
          type="text"
          inputmode=${inputmode}
          autocomplete="off"
          aria-invalid=${error ? 'true' : 'false'}
          aria-describedby=${error ? `${id}-error` : nothing}
          ?disabled=${this.options.busy()}
          .value=${live(String(this.draft[key] ?? ''))}
          @input=${(e: Event) => this.__set(key, (e.target as HTMLInputElement).value)}
        />
      `,
    });
  }

  /** The Company account, Operating account and When prices move panels. */
  render(): TemplateResult {
    const figures = this.options.figures();
    const unit = figures?.unitPricePence ?? null;
    const d = this.draft;
    const credits = (text: string) => parseWhole(text);
    const pence = (text: string) => parsePoundsToPence(text);
    return html`
      <div class="job-panel">
        <h3>Company account</h3>
        <p>When the company account falls below the threshold, the card is charged the target.</p>
        <div class="fields">
          ${this.__renderInput(
            'Threshold (credits)',
            'companyThresholdCredits',
            'companyThresholdCredits',
            [companyThresholdFloorText(figures), creditsWorthText(credits(d.companyThresholdCredits), unit)]
              .filter(Boolean)
              .join(' '),
            'numeric',
          )}
          ${this.__renderInput(
            'Target (£)',
            'companyTarget',
            'companyTargetPence',
            penceBuysText(pence(d.companyTarget), unit),
            'decimal',
          )}
          ${this.__renderInput(
            'Monthly card limit (£)',
            'monthlyCardLimit',
            'monthlyCardLimitPence',
            [
              'The most charged to the card in a calendar month, UK time.',
              penceBuysText(pence(d.monthlyCardLimit), unit),
            ]
              .filter(Boolean)
              .join(' '),
            'decimal',
          )}
        </div>
      </div>
      <div class="job-panel">
        <h3>Operating account</h3>
        <p>
          The operating account pays your company's public ledger fees. When it falls below its threshold, credits move
          to it from the company account, up to its target.
        </p>
        <div class="fields">
          ${this.__renderInput(
            'Threshold (credits)',
            'operatingThresholdCredits',
            'operatingThresholdCredits',
            [
              figures ? `At least ${formatCredits(figures.reserveCredits)}, the reserve kept for fees.` : null,
              creditsWorthText(credits(d.operatingThresholdCredits), unit),
            ]
              .filter(Boolean)
              .join(' '),
            'numeric',
          )}
          ${this.__renderInput(
            'Target (credits)',
            'operatingTargetCredits',
            'operatingTargetCredits',
            creditsWorthText(credits(d.operatingTargetCredits), unit),
            'numeric',
          )}
          ${this.__renderInput(
            'Monthly top-up limit (£)',
            'monthlyTopUpLimit',
            'monthlyTopUpLimitPence',
            [
              'The most moved to the operating account in a calendar month, UK time.',
              penceBuysText(pence(d.monthlyTopUpLimit), unit),
            ]
              .filter(Boolean)
              .join(' '),
            'decimal',
          )}
        </div>
      </div>
      <div class="job-panel">
        <h3>When prices move</h3>
        <wa-radio-group
          label="Ask the signatories to change the target when it buys noticeably fewer credits?"
          orientation="horizontal"
          .value=${d.alertOn === true ? 'YES' : d.alertOn === false ? 'NO' : ''}
          @change=${(e: Event) => this.__set('alertOn', (e.target as HTMLInputElement).value === 'YES')}
        >
          <wa-radio value="YES">Yes</wa-radio>
          <wa-radio value="NO">No</wa-radio>
        </wa-radio-group>
        ${d.alertOn
          ? this.__renderInput(
              'Percentage',
              'alertPercent',
              'targetAlertPercent',
              'A change target card goes to the signatories when the target buys less than this percentage of the credits it buys now',
              'numeric',
            )
          : d.alertOn === null && this.problems.targetAlertPercent
            ? html`
                <div class="field-error" role="alert">${this.problems.targetAlertPercent}</div>
              `
            : nothing}
        <p class="note">
          Whatever you choose, if the target ever buys no more credits than the threshold, charges stop and the
          signatories are asked to change it.
        </p>
      </div>
    `;
  }
}
